import { z } from 'zod/v4';
import { solarPlaneFitSchema } from './solarPlaneFit';
import {
  isRoofBearingRuleKey,
  solarPlacementRulesSchema,
} from './solarPlacementRules';
import { solarMountingSchema, solarRoofPlaneSchema } from './solarMounting';
import { solarGenerationProvenanceSchema } from './solarGenerationAvailability';
import { solarRoofEdgeAnchorSchema } from './solarRoofEdgeAnchor';
import { solarUsNetMeteringPolicySchema } from './solarUsNetMeteringPolicy';
import { solarWeatherGenerationSchema } from './solarWeatherGeneration';
import {
  inspectSolarElectricalTopology,
  solarDesignElectricalSchema,
} from './solarElectricalTopology';

const coordinate = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
const ring = z.array(coordinate).min(3).max(500);
const polygon = z.object({ outer: ring, holes: z.array(ring).max(100) });
const faceId = z.number().int().nonnegative();
const vertex = z.object({
  faceId,
  vertexIndex: z.number().int().nonnegative(),
});
const applianceId = z.string().max(100).nullable();

export const solarInverterSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('automatic') }),
  z.object({ mode: z.literal('none') }),
  z.object({ mode: z.literal('selected'), applianceId: z.string().max(100) }),
]);
export type SolarInverterSelection = z.infer<
  typeof solarInverterSelectionSchema
>;

export const selectedSolarInverterApplianceId = (
  inverter: SolarInverterSelection
): string | null =>
  inverter.mode === 'selected' ? inverter.applianceId : null;

/** The editable working set, including excluded panels and roofs, never an estimate. */
export const solarDesignDraftStateSchema = z
  .object({
    electrical: solarDesignElectricalSchema,
    weatherGeneration: solarWeatherGenerationSchema.optional(),
    layout: z.object({
      workingFaces: z
        .array(
          z.object({
            id: faceId,
            polygon,
            pitchDegrees: z.number().min(0).max(90),
            azimuthDegrees: z.number().min(0).max(360),
            /** Physical roof, separate from the legacy applied panel angles. */
            roofPlane: solarRoofPlaneSchema.optional(),
            planeFit: solarPlaneFitSchema.optional(),
            mounting: solarMountingSchema.optional(),
            /** Baseline array bearing; faceOffsets remain degree offsets from it. */
            arrayAzimuthDegrees: z.number().min(0).max(360).optional(),
            /** Mounting used to create the saved panels, not pending intent. */
            appliedMounting: z.enum(['flush', 'tilt']).optional(),
            /** Last successful local pack; pending edits do not change this. */
            appliedPlacementRules: solarPlacementRulesSchema.optional(),
            roofEdgeAnchor: solarRoofEdgeAnchorSchema.nullish(),
            appliedRoofEdgeAnchor: solarRoofEdgeAnchorSchema.nullish(),
            /** Null means unknown applied direction, never pending intent. */
            appliedArrayAzimuthDegrees: z.number().min(0).max(360).nullish(),
            /** "Include anyway": the roof is packed whatever the skip rules say. */
            rulesOverride: z.literal(true).optional(),
            generation: solarGenerationProvenanceSchema.optional(),
            segmentIndex: z.number().int(),
            areaMeters2: z.number().nonnegative().max(1_000_000),
            isNew: z.boolean(),
            pitchSource: z.enum(['dsm', 'assumed', 'manual']),
            groupId: z.number().int().nullable(),
            shading: z
              .object({
                annualSunAccessPercent: z.number().min(0).max(100),
                monthlySunAccessPercent: z
                  .array(z.number().min(0).max(100))
                  .length(12),
                hourlySunAccessPercent: z
                  .array(z.array(z.number().min(0).max(100)).length(24))
                  .length(12),
              })
              .nullish(),
            photoAgreement: z
              .object({
                agrees: z.string().max(100),
                note: z.string().max(2_000).nullish(),
              })
              .nullish(),
          })
        )
        .max(500),
      workingPanels: z
        .array(
          z.object({
            id: z.uuid().optional(),
            equipmentSelectionId: z.uuid().optional(),
            placementGroupId: z.uuid().optional(),
            corners: z.tuple([coordinate, coordinate, coordinate, coordinate]),
            orientation: z.enum(['PORTRAIT', 'LANDSCAPE']),
            faceIndex: z.number().int().nonnegative(),
            faceId,
            segmentIndex: z.number().int(),
            annualAcKwh: z.number().nonnegative().max(1_000_000),
            mcsYearlyEnergyKwh: z.number().nonnegative().optional(),
            included: z.boolean(),
          })
        )
        .max(5_000),
      workingObstacles: z.array(ring).max(500),
      faceOffsets: z.record(
        z.string().regex(/^\d+$/),
        z.number().min(-360).max(360)
      ),
      faceLinks: z.array(z.object({ a: vertex, b: vertex })).max(2_000),
      excludedFaceIds: z.array(faceId).max(500),
      manualMode: z.boolean(),
      geometryEdited: z.boolean(),
    }),
    selectedPanelApplianceId: applianceId,
    inverter: solarInverterSelectionSchema,
    selectedBatteryApplianceId: applianceId,
    includeBattery: z.boolean(),
    batteryQuantity: z.number().int().positive().max(2_147_483_647),
    configIndex: z.number().int().nonnegative().nullable(),
    selectedPanelIndexes: z.array(z.number().int().nonnegative()).max(5_000),
    placementRules: solarPlacementRulesSchema,
    /** Last packed bearing rule per roof. */
    roofBearingRuleByFace: z.record(
      z.string().regex(/^(0|[1-9]\d*)$/),
      z
        .string()
        .max(100)
        .refine(
          isRoofBearingRuleKey,
          'Use a canonical packed roof-bearing rule'
        )
    ),
    monthlyPolicy: solarUsNetMeteringPolicySchema.nullable(),
  })
  .superRefine(({ layout, electrical }, ctx) => {
    if (electrical.mode === 'allocated')
      for (const message of inspectSolarElectricalTopology(
        electrical,
        layout.workingPanels,
        layout.workingFaces.map((face) => face.id),
        layout.excludedFaceIds
      ).errors)
        ctx.addIssue({ code: 'custom', message });
    if (layout.workingFaces.some((face) => face.mounting && !face.roofPlane)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Confirm the physical roof plane before choosing mounting',
      });
    }
    const faces = new Map(layout.workingFaces.map((face) => [face.id, face]));
    if (faces.size !== layout.workingFaces.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Roof identities must be unique',
      });
    }
    if (
      layout.workingPanels.some((panel) => !faces.has(panel.faceId)) ||
      layout.excludedFaceIds.some((id) => !faces.has(id)) ||
      Object.keys(layout.faceOffsets).some((id) => !faces.has(Number(id))) ||
      layout.faceLinks.some((link) =>
        [link.a, link.b].some(
          (end) =>
            !faces.has(end.faceId) ||
            end.vertexIndex >= faces.get(end.faceId)!.polygon.outer.length
        )
      )
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'The layout references a missing roof or vertex',
      });
    }
  });

export type SolarDesignDraftState = z.infer<typeof solarDesignDraftStateSchema>;
